import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { trackDownloadClick } from '../utils/analytics';

// Apple App Store SVG
const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current shrink-0" aria-hidden="true">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

// Google Play SVG
const PlayIcon = () => (
  <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current shrink-0" aria-hidden="true">
    <path d="M3.18 23.76c.37.21.8.22 1.2.04l12.94-7.47-2.93-2.93-11.21 10.36zm-1.01-20.6C2.06 3.44 2 3.73 2 4.04v15.92c0 .31.06.6.17.87l.08.08 8.91-8.91v-.21L2.25 3.08l-.08.08zM20.49 10.7l-2.68-1.55-3.18 3.18 3.18 3.18 2.7-1.56c.77-.45.77-1.8-.02-2.25zm-17.31 12.1l12.94-7.46-2.93-2.94L3.18.24c-.4-.18-.83-.17-1.2.04l-.01.01 11.21 10.37-10 9.14z"/>
  </svg>
);

const DownloadSparkIcon = () => (
  <svg viewBox="0 0 64 64" className="w-12 h-12 shrink-0" aria-hidden="true">
    <defs>
      <linearGradient id="downloadSparkGradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#60A5FA" />
        <stop offset="55%" stopColor="#22D3EE" />
        <stop offset="100%" stopColor="#A78BFA" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="30" fill="url(#downloadSparkGradient)" />
    <path d="M32 14v22" stroke="#0B1020" strokeWidth="5" strokeLinecap="round" />
    <path d="M22 30l10 10 10-10" fill="none" stroke="#0B1020" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
    <rect x="18" y="46" width="28" height="4" rx="2" fill="#0B1020" />
  </svg>
);

// Minimal top bar — just logo + one escape link
const TopBar = () => (
  <nav className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-6 sm:px-10 py-5">
    <Link to="/" className="text-indigo-200 font-serif text-xl tracking-wider hover:text-white transition-colors">
      Andlegur
    </Link>
    <Link to="/login" className="text-indigo-400 hover:text-indigo-200 text-sm transition-colors">
      Sign in
    </Link>
  </nav>
);

export default function AppDownloadPage() {
  const handleIOS = () => { trackDownloadClick('ios'); window.open('https://apps.apple.com/us/app/andlegur/id6759088599', '_blank', 'noopener,noreferrer'); };
  const handleAndroid = () => { trackDownloadClick('android'); window.open('https://play.google.com/store/apps/details?id=com.andlegur.mobile', '_blank', 'noopener,noreferrer'); };

  return (
    <>
      <Helmet>
        <title>Download Andlegur | iOS & Android</title>
        <meta name="description" content="Download Andlegur for iOS and Android. Reflect privately on your phone, gain clarity, and make better life decisions anywhere." />
        <meta name="keywords" content="download Andlegur, Andlegur app, iOS reflection app, Android reflection app, personal clarity app" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://andlegur.com/download" />
        <meta property="og:title" content="Download Andlegur — iOS & Android" />
        <meta property="og:description" content="A private mobile space for reflection, clarity, and personal direction." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://andlegur.com/download" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Download Andlegur — iOS & Android" />
        <meta name="twitter:description" content="Download Andlegur and start reflecting privately on your phone today." />
        <meta name="apple-itunes-app" content="app-id=6759088599" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Andlegur",
            "operatingSystem": ["iOS", "ANDROID"],
            "applicationCategory": "LifestyleApplication",
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
            "description": "Andlegur is a digital sanctuary for reflection, emotional well-being, and personal growth.",
            "url": "https://andlegur.com/download",
            "downloadUrl": [
              "https://apps.apple.com/us/app/andlegur/id6759088599",
              "https://play.google.com/store/apps/details?id=com.andlegur.mobile"
            ]
          })}
        </script>
      </Helmet>

      <div className="relative min-h-screen overflow-hidden bg-[#05091B] font-serif text-indigo-100">
        <TopBar />

        <div className="absolute inset-0 z-0 pointer-events-none">
          <img
            src="/web_images/Andlegur%20web%2010.jpg"
            alt=""
            className="w-full h-full object-cover opacity-25 mix-blend-screen"
            loading="eager"
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-linear-to-br from-[#05091B]/20 via-transparent to-[#05091B]/90" />
          <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/60" />
        </div>

        <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 sm:px-10 pt-24 pb-10">
          <div className="w-full max-w-4xl bg-[#0E1833]/85 backdrop-blur-md border border-indigo-500/40 rounded-3xl p-6 sm:p-10 lg:p-12 text-center shadow-2xl">
            <p className="text-indigo-300 text-sm sm:text-base tracking-wide mb-5">Andlegur • Free • iOS & Android</p>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] mb-6">
              Download The App.
              <br />
              Provide Feedbacks
            </h1>

         

            <div className="flex flex-col">
              <div className="order-1 md:order-2 mb-8">
                <div className="flex justify-center mb-4">
                  <DownloadSparkIcon />
                </div>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button
                    onClick={handleIOS}
                    className="group flex items-center gap-4 border-2 border-purple-300 bg-blue-50 text-black hover:bg-indigo-50 px-7 py-4 rounded-2xl font-semibold transition-all duration-300 hover:scale-105 shadow-2xl shadow-indigo-900/50 cursor-pointer justify-center sm:justify-start"
                    aria-label="Download Andlegur on the App Store"
                  >
                    <AppleIcon />
                    <div className="text-left">
                      <p className="text-sm opacity-70 leading-none mb-0.5">Download on the</p>
                      <p className="text-xl font-bold leading-none">App Store</p>
                    </div>
                  </button>

                  <button
                    onClick={handleAndroid}
                    className="group flex items-center gap-4 bg-black text-blue-100 hover:bg-indigo-950 px-7 py-4 rounded-2xl font-semibold transition-all duration-300 hover:scale-105 shadow-2xl shadow-indigo-900/50 cursor-pointer justify-center sm:justify-start"
                    aria-label="Get Andlegur on Google Play"
                  >
                    <PlayIcon />
                    <div className="text-left">
                      <p className="text-sm opacity-70 leading-none mb-0.5">Get it on</p>
                      <p className="text-xl font-bold leading-none">Google Play</p>
                    </div>
                  </button>
                </div>
              </div>

              <div className="order-2 md:order-1 grid gap-3 text-left max-w-xl mx-auto mb-6 text-indigo-100 text-base sm:text-lg">
                <p className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-300" />Understand your thoughts quickly</p>
                <p className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-300" />Find direction in important decisions</p>
                <p className="flex items-start gap-3"><span className="mt-1 h-2 w-2 rounded-full bg-indigo-300" />Reflect and grow privately on mobile</p>
              </div>
            </div>

            <p className="text-indigo-300 text-sm sm:text-base">Private • Free • Your reflections stay yours</p>

          </div>

          <Link to="/register" className="mt-5 text-indigo-300 hover:text-white underline underline-offset-4 text-sm sm:text-base">
            Create your free account
          </Link>
        </div>
      </div>
    </>
  );
}
